import type { Currency } from '@ps/web-core-enums';
import type { TranslationTag } from '@ps/web-core-intl';
import type { Error } from '@ps/web-shared-validators';
import { isArray, isEmpty } from 'lodash';

import type { BonusInfo, PreCashinInfo } from '../types/Cashier';

import {
  TXTCLI_CongratulationsX_Once_your_dep_ELL,
  TXTCLI_The_first_N0_of_your_deposit_e_ELL,
  TXTCLI_The_first_N0_of_your_deposit_e_ELL_1,
  TXTCLI_The_first_N0_of_your_deposit_e_ELL_2,
  TXTCLI_The_first_N0_of_your_deposit_e_ELL_5,
  TXTCLI_The_first_N0_of_your_deposit_e_ELL_6,
  TXTCLI_The_first_N0_of_your_deposit_q_ELL,
  TXTCLI_The_first_N0_of_your_deposit_q_ELL_1,
  TXTCLI_The_first_N0_of_your_deposit_q_ELL_2,
  TXTCLI_The_first_N0_of_your_deposit_q_ELL_5,
  TXTCLI_The_first_N0_of_your_deposit_q_ELL_6,
  TXTCLI_The_maximum_number_of_ticket_s_ELL,
  TXTCLI_The_maximum_number_of_tickets__ELL,
  TXTCLI_The_maximum_number_of_tickets__ELL_1,
  TXTCLI_This_will_be_Xb_classXXredXXth_ELL,
  TXTCLI_This_will_be_Xb_classXXredXXth_ELL_1,
  TXTCLI_This_will_be_Xb_classXXredXXth_ELL_4,
  TXTCLI_This_will_be_Xb_classXXredXXth_ELL_5,
  TXTCLI_XbXYou_may_continue_to_make_de_ELL_1,
  TXTCLI_XbXYou_may_continue_to_make_de_ELL_2,
  TXTCLI_XbXYou_may_continue_to_make_de_ELL_5,
  TXTCLI_XbXYou_may_continue_to_make_de_ELL_6,
  TXTCLI_You_can_collect_your_bonus_of__ELL,
  TXTCLI_You_can_collect_your_bonus_of__ELL_1,
  TXTCLI_You_may_continue_to_make_depos_ELL_1,
  TXTCLI_You_may_continue_to_make_depos_ELL_2,
  TXTCLI_You_may_continue_to_make_depos_ELL_5,
  TXTCLI_You_may_continue_to_make_depos_ELL_6,
  TXTCLI_You_receive_1_ticket_per_every_ELL_1,
  TXTCLI_You_receive_1_ticket_set_per_e_ELL,
  TXTCLI_You_receive_N1_ticket_sets_per_ELL,
  TXTCLI_You_receive_N1_tickets_per_eve_ELL,
  TXTCLI_You_will_receive_1_ticket_set__ELL,
  TXTCLI_You_will_receive_N1_ticket_set_ELL,
  TXTCLI_Your_N0_deposit_earns_you_a_bo_ELL,
  TXTCLI_Your_N0_deposit_earns_you_a_bo_ELL_1,
  TXTCLI_Your_N0_deposit_earns_you_a_bo_ELL_2,
  TXTCLI_Your_N0_deposit_earns_you_a_bo_ELL_3,
  TXTCLI_Your_N0_deposit_earns_you_a_bo_ELL_4,
  TXTCLI_Your_N0_deposit_qualifies_you__ELL,
  TXTCLI_Your_N0_deposit_qualifies_you__ELL_1,
  TXTCLI_Your_N0_deposit_qualifies_you__ELL_10,
  TXTCLI_Your_N0_deposit_qualifies_you__ELL_2,
  TXTCLI_Your_N0_deposit_qualifies_you__ELL_9,
  TXTCLI_Your_deposit_of_N0_has_allowed_ELL,
  TXTWCL_Please_change_your_payment_met_ELL,
  TXTWCL_The_maximum_you_can_deposit_wi_ELL,
  TXTWCL_This_code_can_only_be_redeemed_ELL_1,
  TXTWCL_This_code_can_only_be_redeemed_ELL_2,
  TXTWCL_You_need_to_deposit_a_minimum__ELL
} from './bonus.messages.json';

export const isSingleTicket = ({ ticketSet }: PreCashinInfo) => {
  if (!isArray(ticketSet) || ticketSet.length !== 1) {
    return false;
  }

  return ticketSet[0].numOfTickets === 1;
};

export enum BonusFlags {
  /* eslint-disable no-bitwise */
  GradualDepositAllowed = 1 << 6,
  /* eslint-disable no-bitwise */
  TournamentTicketOnly = 1 << 7
}

type MessageGroup = Record<0 | 1 | 2 | 3, TranslationTag>;

export enum Messages {
  RewardFirst = 'RewardFirst',
  RewardYour = 'RewardYour',
  TicketsFirst = 'TicketsFirst',
  TicketsYour = 'TicketsYour',
  TicketsCollect = 'TicketsCollect',
  CashFirst = 'CashFirst',
  CashYour = 'CashYour',
  TicketsDetails = 'TicketsDetails',
  PortionAmount = 'PortionAmount',
  EligibleDeposits = 'EligibleDeposits',
  LastEligibleDeposits = 'LastEligibleDeposits',
  FinalEligibleDeposit = 'FinalEligibleDeposit'
}

const MessageGroups: { [key in Messages]: MessageGroup } = {
  [Messages.RewardFirst]: {
    0: TXTCLI_The_first_N0_of_your_deposit_e_ELL_1,
    1: TXTCLI_The_first_N0_of_your_deposit_e_ELL_2,
    2: TXTCLI_The_first_N0_of_your_deposit_e_ELL_5,
    3: TXTCLI_The_first_N0_of_your_deposit_e_ELL_6
  },
  [Messages.RewardYour]: {
    0: TXTCLI_Your_N0_deposit_earns_you_a_bo_ELL_1,
    1: TXTCLI_Your_N0_deposit_earns_you_a_bo_ELL_2,
    2: TXTCLI_Your_N0_deposit_earns_you_a_bo_ELL_3,
    3: TXTCLI_Your_N0_deposit_earns_you_a_bo_ELL_4
  },
  [Messages.TicketsFirst]: {
    0: TXTCLI_The_first_N0_of_your_deposit_q_ELL_1,
    1: TXTCLI_The_first_N0_of_your_deposit_q_ELL_2,
    2: TXTCLI_The_first_N0_of_your_deposit_q_ELL_5,
    3: TXTCLI_The_first_N0_of_your_deposit_q_ELL_6
  },
  [Messages.TicketsYour]: {
    0: TXTCLI_Your_N0_deposit_qualifies_you__ELL_1,
    1: TXTCLI_Your_N0_deposit_qualifies_you__ELL_2,
    2: TXTCLI_Your_N0_deposit_qualifies_you__ELL_9,
    3: TXTCLI_Your_N0_deposit_qualifies_you__ELL_10
  },
  [Messages.TicketsCollect]: {
    0: TXTCLI_You_can_collect_your_bonus_of__ELL,
    1: TXTCLI_You_can_collect_your_bonus_of__ELL_1,
    2: TXTCLI_You_will_receive_1_ticket_set__ELL,
    3: TXTCLI_You_will_receive_N1_ticket_set_ELL
  },
  [Messages.CashFirst]: {
    0: TXTCLI_The_first_N0_of_your_deposit_q_ELL,
    1: TXTCLI_The_first_N0_of_your_deposit_q_ELL_1,
    2: TXTCLI_The_first_N0_of_your_deposit_e_ELL,
    3: TXTCLI_The_first_N0_of_your_deposit_q_ELL_5
  },
  [Messages.CashYour]: {
    0: TXTCLI_Your_N0_deposit_qualifies_you__ELL,
    1: TXTCLI_CongratulationsX_Once_your_dep_ELL,
    2: TXTCLI_Your_N0_deposit_earns_you_a_bo_ELL,
    3: TXTCLI_Your_deposit_of_N0_has_allowed_ELL
  },
  [Messages.TicketsDetails]: {
    0: TXTCLI_The_maximum_number_of_tickets__ELL,
    1: TXTCLI_The_maximum_number_of_tickets__ELL_1,
    2: TXTCLI_The_maximum_number_of_ticket_s_ELL,
    3: TXTCLI_The_maximum_number_of_ticket_s_ELL
  },
  [Messages.PortionAmount]: {
    0: TXTCLI_You_receive_1_ticket_per_every_ELL_1,
    1: TXTCLI_You_receive_N1_tickets_per_eve_ELL,
    2: TXTCLI_You_receive_1_ticket_set_per_e_ELL,
    3: TXTCLI_You_receive_N1_ticket_sets_per_ELL
  },
  [Messages.EligibleDeposits]: {
    0: TXTCLI_XbXYou_may_continue_to_make_de_ELL_1,
    1: TXTCLI_XbXYou_may_continue_to_make_de_ELL_2,
    2: TXTCLI_XbXYou_may_continue_to_make_de_ELL_5,
    3: TXTCLI_XbXYou_may_continue_to_make_de_ELL_6
  },
  [Messages.LastEligibleDeposits]: {
    0: TXTCLI_You_may_continue_to_make_depos_ELL_1,
    1: TXTCLI_You_may_continue_to_make_depos_ELL_2,
    2: TXTCLI_You_may_continue_to_make_depos_ELL_6,
    3: TXTCLI_You_may_continue_to_make_depos_ELL_5
  },
  [Messages.FinalEligibleDeposit]: {
    0: TXTCLI_This_will_be_Xb_classXXredXXth_ELL,
    1: TXTCLI_This_will_be_Xb_classXXredXXth_ELL_1,
    2: TXTCLI_This_will_be_Xb_classXXredXXth_ELL_5,
    3: TXTCLI_This_will_be_Xb_classXXredXXth_ELL_4
  }
};

export const getBonusMessage = (
  message: Messages,
  isSingleTicket: boolean,
  condition: boolean
): TranslationTag => {
  const messages: MessageGroup = MessageGroups[message];

  if (isSingleTicket) {
    return messages[condition ? 0 : 1];
  }

  return messages[condition ? 2 : 3];
};

export const checkBonusData = (
  { currencySpecifics, paySystems }: BonusInfo,
  allValues: object
): Error => {
  const {
    deposit: { amount, currency, type, bonusCode }
  } = allValues as {
    deposit: {
      amount: number;
      bonusCode: string;
      currency: Currency;
      type: number;
    };
  };

  const currencyDetails = currencySpecifics[currency];

  if (isEmpty(currencySpecifics) || !bonusCode) {
    return { valid: true };
  }

  if (!currencyDetails) {
    const currencies = Object.keys(currencySpecifics);
    if (currencies.length === 1) {
      return {
        valid: false,
        message: {
          ...TXTWCL_This_code_can_only_be_redeemed_ELL_1,
          values: { 0: currencies[0] }
        }
      };
    }

    const firstCurrencies = currencies
      .filter((elem) => elem !== currencies[currencies.length - 1])
      .join(', ');

    return {
      valid: false,
      message: {
        ...TXTWCL_This_code_can_only_be_redeemed_ELL_2,
        values: { 0: firstCurrencies, 1: currencies[currencies.length - 1] }
      }
    };
  } else if (Number(amount) > currencyDetails.maxAmount / 100) {
    return {
      valid: false,
      message: {
        ...TXTWCL_The_maximum_you_can_deposit_wi_ELL,
        values: { 0: currency + ' ' + currencyDetails.maxAmount / 100 }
      }
    };
  } else if (Number(amount) < currencyDetails.minAmount / 100) {
    return {
      valid: false,
      message: {
        ...TXTWCL_You_need_to_deposit_a_minimum__ELL,
        values: { 0: currency + ' ' + currencyDetails.minAmount / 100 }
      }
    };
  }

  if (!isEmpty(paySystems) && !paySystems.includes(type)) {
    return { valid: false, message: TXTWCL_Please_change_your_payment_met_ELL };
  }

  return { valid: true };
};
